import apiClient from '@/utils/apiClient';

/**
 * Extracts products that should trigger agent notification after publish.
 * Includes new product codes and sale price changes only.
 *
 * @param {Array<Object>} drafts
 * @returns {Array<Object>}
 */
export function extractPriceListNotificationChanges(drafts = []) {
    const changes = [];

    drafts.forEach((draft) => {
        const productCode = String(
            draft.payload?.product_code || draft.product_code || '',
        ).trim();
        if (!productCode) {
            return;
        }

        const productName = draft.payload?.product_name
            || draft.summary?.product_name
            || '';
        const newPrice = Number(draft.payload?.price);
        const previousPriceRaw = draft.previousPrice ?? draft.previous_price ?? null;
        const previousPrice = previousPriceRaw == null || previousPriceRaw === ''
            ? null
            : Number(previousPriceRaw);
        const isNewProduct = draft.action === 'create';

        if (!Number.isFinite(newPrice)) {
            return;
        }

        if (isNewProduct) {
            changes.push({
                product_code: productCode,
                product_name: productName,
                old_price: null,
                new_price: newPrice,
                is_new_product: true,
            });
            return;
        }

        if (previousPrice == null || Math.abs(previousPrice - newPrice) <= 0.001) {
            return;
        }

        changes.push({
            product_code: productCode,
            product_name: productName,
            old_price: previousPrice,
            new_price: newPrice,
            is_new_product: false,
        });
    });

    return changes;
}

/**
 * Notify agents about published price list changes.
 *
 * @param {Object} params
 * @param {number|string} params.priceListId
 * @param {boolean} [params.isNewList]
 * @param {Array<Object>} params.changes
 * @param {boolean} [params.test]
 * @returns {Promise<Object>}
 */
export async function notifyPriceListChanges({
    priceListId,
    isNewList = false,
    changes = [],
    test = true,
}) {
    if (!priceListId || !changes.length) {
        return null;
    }

    const response = await apiClient.post('/api/notify-price-list-changes/', {
        price_list_id: priceListId,
        is_new_list: isNewList,
        changes,
        test,
    });
    return response.data;
}
